"use client";

import * as React from "react";
import { cn } from "lib/utils";
import visualStack from "constants/visual-stack";
import { Button } from "./button";

type DrawerProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
};

const DrawerContext = React.createContext<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}>({
  open: false,
  onOpenChange: () => {},
});

const Drawer = ({ open, onOpenChange, children, ...props }: DrawerProps) => {
  const [internalOpen, setInternalOpen] = React.useState(false);

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [isControlled, onOpenChange]
  );

  return (
    <DrawerContext.Provider
      value={{ open: isOpen, onOpenChange: handleOpenChange }}
    >
      {children}
    </DrawerContext.Provider>
  );
};
Drawer.displayName = "Drawer";

const useDrawerContext = () => {
  const context = React.useContext(DrawerContext);
  if (!context) {
    throw new Error("Drawer components must be used within a Drawer");
  }
  return context;
};

const DrawerTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ ...props }, ref) => {
  const { onOpenChange } = useDrawerContext();

  return (
    <Button
      variant="ghost"
      ref={ref}
      onClick={() => onOpenChange(true)}
      {...props}
    />
  );
});
DrawerTrigger.displayName = "DrawerTrigger";

const DrawerPortal: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <>{children}</>;
};
DrawerPortal.displayName = "DrawerPortal";

const DrawerClose = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ ...props }, ref) => {
  const { onOpenChange } = useDrawerContext();

  return (
    <button
      type="button"
      ref={ref}
      onClick={() => onOpenChange(false)}
      {...props}
    />
  );
});
DrawerClose.displayName = "DrawerClose";

const DrawerOverlay = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, id, ...props }, ref) => {
  const { open, onOpenChange } = useDrawerContext();

  if (!open) return null;

  return (
    <div
      ref={ref}
      className={cn("fixed inset-0 z-40 bg-black/80", className)}
      style={{ height: "100vh", overflowY: "hidden" }}
      onClick={() => {
        if (id && visualStack.peek() !== id) return;

        visualStack.pop();
        onOpenChange(false);
      }}
      {...props}
    />
  );
});
DrawerOverlay.displayName = "DrawerOverlay";

const DrawerContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, id, ...props }, ref) => {
  const { open } = useDrawerContext();
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [startY, setStartY] = React.useState(0);
  const [currentY, setCurrentY] = React.useState(0);
  const { onOpenChange } = useDrawerContext();

  // Keep track of the actual DOM node
  const drawerRef = React.useRef<HTMLDivElement | null>(null);
  const combinedRef = (node: HTMLDivElement) => {
    // Forward the ref to the parent component
    if (typeof ref === "function") {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }

    // Store the ref for our own use
    drawerRef.current = node;
    contentRef.current = node;
  };

  // Animation state
  const [visible, setVisible] = React.useState(open);

  React.useEffect(() => {
    // Prevent body scrolling when drawer is open
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  React.useEffect(() => {
    if (open) {
      setVisible(true);
      if (id) visualStack.push(id);

      // Set transform immediately on mount, but outside the initial render
      if (drawerRef.current) {
        requestAnimationFrame(() => {
          if (drawerRef.current) {
            // First set to "down" position
            drawerRef.current.style.transform = "translateY(100%)";

            // Force a reflow to ensure the initial transform is applied
            // eslint-disable-next-line no-unused-expressions
            drawerRef.current.offsetHeight;

            // Then animate it up in the next frame
            requestAnimationFrame(() => {
              if (drawerRef.current) {
                drawerRef.current.style.transform = "translateY(0)";
              }
            });
          }
        });
      }
    } else if (visible) {
      // Animate out
      if (drawerRef.current) {
        drawerRef.current.style.transform = "translateY(100%)";
      }

      // Wait for animation to complete before removing from DOM
      const timer = setTimeout(() => {
        setVisible(false);
      }, 300); // Match the transition duration

      return () => clearTimeout(timer);
    }
  }, [open, id, visible]);

  // Handle drag to dismiss
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
    setCurrentY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging) {
      setCurrentY(e.touches[0].clientY);

      const dragDistance = currentY - startY;
      if (dragDistance > 0) {
        if (contentRef.current) {
          contentRef.current.style.transform = `translateY(${dragDistance}px)`;
        }
      }
    }
  };

  const handleTouchEnd = () => {
    if (isDragging) {
      const dragDistance = currentY - startY;

      if (dragDistance > 100) {
        // Close the drawer if dragged down enough
        onOpenChange(false);
      } else {
        // Reset position
        if (contentRef.current) {
          contentRef.current.style.transform = "translateY(0)";
        }
      }

      setIsDragging(false);
    }
  };

  if (!visible) return null;

  return (
    <DrawerPortal>
      <DrawerOverlay id={id} />
      <div
        ref={combinedRef}
        data-drawer-content="true"
        className={cn(
          "fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto max-h-[85vh] flex-col rounded-t-[10px] border bg-background overflow-auto",
          className
        )}
        style={{
          // Initial styles - starting at natural position, animation handled in useEffect
          transition: isDragging
            ? "none"
            : "transform 300ms cubic-bezier(0.16, 1, 0.3, 1)",
          willChange: "transform",
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        {...props}
      >
        <div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
        {children}
      </div>
    </DrawerPortal>
  );
});
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)}
    {...props}
  />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("mt-auto flex flex-col gap-2 p-4", className)}
    {...props}
  />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h2
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DrawerTitle.displayName = "DrawerTitle";

const DrawerDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DrawerDescription.displayName = "DrawerDescription";

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
